import { useEffect, useState } from "react";
import { ArrowRightLeft, Check, X, AlertCircle, Clock, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_PILL = {
  pending:   { bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500",   label: "Pending" },
  completed: { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", label: "Completed" },
  rejected:  { bg: "bg-rose-100",    text: "text-rose-800",    dot: "bg-rose-500",    label: "Rejected" },
  cancelled: { bg: "bg-slate-100",   text: "text-slate-700",   dot: "bg-slate-400",   label: "Cancelled" },
};

const MEMBERSHIP_HANDLING = [
  { value: "leave_with_existing", label: "Leave membership with existing owner" },
  { value: "end", label: "End existing owner's membership at period end" },
  { value: "transfer", label: "Transfer membership to new owner" },
];

export default function AdminTransfers() {
  const [transfers, setTransfers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/transfers${statusFilter ? `?status=${statusFilter}` : ""}`)
      .then(({ data }) => setTransfers(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const approve = async (id) => {
    if (!window.confirm("Approve this transfer? Ownership will move immediately and is permanent.")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/transfers/${id}/approve`);
      toast.success("Transfer approved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection? (optional, visible in audit log)");
    if (reason === null) return; // cancel
    setBusyId(id);
    try {
      await api.post(`/admin/transfers/${id}/reject?reason=${encodeURIComponent(reason || "")}`);
      toast.success("Transfer rejected");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally { setBusyId(null); }
  };

  return (
    <PortalShell>
      <div data-testid="admin-transfers-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-600">Property Transfer Queue</div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Property transfers</h1>
            <p className="text-slate-500 mt-1 text-sm">Approve, reject or initiate AWARE Water Health Record property transfers.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value=" ">All</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="admin-transfer-create-btn" onClick={() => setShowCreate(true)} className="btn-aqua rounded-full font-semibold">
              <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Direct transfer
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-7 bg-white rounded-3xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Property</th>
                  <th className="text-left px-5 py-3">From</th>
                  <th className="text-left px-5 py-3">To</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Initiated</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-12 text-slate-500">Loading...</td></tr>
                ) : transfers.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                    <ArrowRightLeft className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                    No transfers in this state.
                  </td></tr>
                ) : (
                  transfers.map((t) => {
                    const pill = STATUS_PILL[t.status] || STATUS_PILL.pending;
                    return (
                      <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                        <td className="px-5 py-4">
                          <div className="font-medium text-slate-900">{t.property_address || "—"}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{(t.property_id || "").slice(0, 8)}</div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-slate-700">{t.from_user?.full_name || "—"}</div>
                          <div className="text-xs text-slate-500">{t.from_user?.email}</div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-slate-700">{t.to_user?.full_name || "—"}</div>
                          <div className="text-xs text-slate-500">{t.to_email}</div>
                          {!t.to_user && (
                            <div className="text-[10px] text-rose-600 font-semibold mt-0.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Recipient has no account yet</div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${pill.bg} ${pill.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${pill.dot}`} />
                            {pill.label}
                          </span>
                          {t.initiator_role === "admin" && <span className="ml-1.5 text-[10px] text-slate-400">admin</span>}
                        </td>
                        <td className="px-5 py-4 text-slate-700 text-xs">
                          {formatDateAU(t.requested_at)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          {t.status === "pending" && (
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                data-testid={`transfer-approve-${t.id}`}
                                onClick={() => approve(t.id)}
                                disabled={busyId === t.id || !t.to_user}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Check className="w-3.5 h-3.5" /> Approve
                              </button>
                              <button
                                data-testid={`transfer-reject-${t.id}`}
                                onClick={() => reject(t.id)}
                                disabled={busyId === t.id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-rose-400 hover:text-rose-700 text-slate-700 text-xs font-semibold"
                              >
                                <X className="w-3.5 h-3.5" /> Reject
                              </button>
                            </div>
                          )}
                          {t.status === "completed" && (
                            <div className="text-[11px] text-emerald-700 font-semibold">Completed {formatDateAU(t.completed_at)}</div>
                          )}
                          {t.status === "rejected" && (
                            <div className="text-[11px] text-rose-700">{t.rejected_reason || "Rejected"}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreate && <DirectTransferModal onClose={() => { setShowCreate(false); load(); }} />}
    </PortalShell>
  );
}


function DirectTransferModal({ onClose }) {
  const [propertyId, setPropertyId] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [membershipHandling, setMembershipHandling] = useState("leave_with_existing");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!propertyId || !toEmail) {
      toast.error("Property ID and recipient email are both required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/properties/${propertyId}/transfer`, {
        property_id: propertyId,
        to_email: toEmail,
        notes,
        membership_handling: membershipHandling,
      });
      toast.success("Transfer completed");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transfer failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="admin-direct-transfer-modal" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-7 lg:p-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-600">Admin direct transfer</div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Move property to new owner</h2>
            <p className="text-sm text-slate-500 mt-1">Executes immediately. Recipient must already have an account.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Property ID</Label>
            <Input data-testid="direct-property-id" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} placeholder="e.g. c53203e2-…" className="mt-1.5 font-mono text-sm" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">New owner email</Label>
            <Input data-testid="direct-to-email" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="newowner@example.com" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Membership handling</Label>
            <Select value={membershipHandling} onValueChange={setMembershipHandling}>
              <SelectTrigger data-testid="direct-membership-handling" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{MEMBERSHIP_HANDLING.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Notes</Label>
            <textarea data-testid="direct-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5 w-full rounded-xl border-slate-200 text-sm p-3" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="direct-submit" onClick={submit} disabled={submitting} className="btn-aqua rounded-full font-semibold flex-1">
              {submitting ? "Transferring..." : "Execute transfer"}
            </Button>
            <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
